import { apiClient } from "@/lib/apiClient";
import { Page } from "@/types/models/page";
import { getApiUrl } from "@/config/env";

export class PageService {
  static async uploadFeaturedImage(
    pageId: string,
    imageFile: File,
  ): Promise<string> {
    const formData = new FormData();
    formData.append("image", imageFile);

    // Use fetch directly for file upload since the api client doesn't handle FormData properly
    const API_BASE_URL = getApiUrl();
    const storeId = "1"; // This should come from context/store

    const response = await fetch(
      `${API_BASE_URL}/api/v1/admin/${storeId}/web-builder/pages/${pageId}/featured-image`,
      {
        method: "POST",
        body: formData,
        // Don't set Content-Type header - let browser set it for FormData
      },
    );

    if (!response.ok) {
      throw new Error("Failed to upload image");
    }

    const data = await response.json();
    return data.url || data.path || "";
  }

  static async getFeaturedImage(pageId: string): Promise<string> {
    const response = await apiClient.get<Blob>(
      `/api/v1/admin/:storeId/web-builder/pages/${pageId}/featured-image`,
    );

    // Convert blob to URL for display
    return URL.createObjectURL(response.data);
  }

  static async createBlankPage(pageData: {
    title: string;
    slug: string;
    meta_title?: string;
    is_published?: boolean;
  }): Promise<Page> {
    const response = await apiClient.post<Page>(
      `/api/v1/admin/:storeId/web-builder/pages`,
      pageData,
    );

    return response.data;
  }

  static async deletePage(pageId: string): Promise<void> {
    await apiClient.delete(
      `/api/v1/admin/:storeId/web-builder/pages/${pageId}`,
    );
  }

  static async createPagesFromTemplate(
    templateId: string,
    pageIds?: string[],
  ): Promise<Page[]> {
    const requestBody = pageIds ? { page_ids: pageIds } : {};
    const response = await apiClient.post<Page[]>(
      `/api/v1/admin/:storeId/web-builder/pages/from-template/${templateId}`,
      requestBody,
    );

    return response.data;
  }

  static async createPageFromTemplate(pageData: {
    name: string;
    slug: string;
    title: string;
    description?: string;
    featured_image?: string;
    featured_image_asset_id?: string;
    status?: "draft" | "published" | "archived";
    template_id?: string;
    template_page_id?: string;
  }): Promise<Page> {
    const response = await apiClient.post<Page>(
      `/api/v1/admin/:storeId/web-builder/pages`,
      pageData,
    );

    return response.data;
  }

  static async checkSlugAvailability(slug: string): Promise<{
    available: boolean;
    message: string;
  }> {
    const response = await apiClient.get<{
      message: string;
      valid: boolean;
      data: {
        slug: string;
        available: boolean;
        message: string;
      };
    }>(
      `/api/v1/admin/:storeId/web-builder/pages/check-slug?slug=${encodeURIComponent(slug)}`,
    );

    return {
      available: response.data.data.available,
      message: response.data.data.message,
    };
  }
}
